import { useEffect, useMemo } from 'react';
import DOMPurify from 'dompurify';

const WholesaleAgreementPage = () => {
  useEffect(() => {
    document.title = 'Wholesale Agreement | Only Skullz';
  }, []);

  const agreementHTML = `
    <button class="print-button" onclick="window.print()">Print / Save as PDF</button>
    
    <div class="container">
        <!-- PAGE 1: Header, Parties, Terms 1-7 -->
        <div class="header">
            <h1>Only Skullz <span class="wholesale-badge">Wholesale</span></h1>
            <p>Wholesale Purchase Agreement & Authorization Form</p>
        </div>
        
        <div class="party-grid">
            <div class="party-box">
                <h3>Seller (Only Skullz LLC)</h3>
                <div class="form-group">
                    <label>Business Name</label>
                    <input type="text" value="Only Skullz LLC" readonly>
                </div>
                <div class="compact-row">
                    <div class="form-group">
                        <label>Email</label>
                        <input type="email" value="Angel@OnlySkullz.com" readonly>
                    </div>
                    <div class="form-group">
                        <label>Phone</label>
                        <input type="tel" value="630-945-1224" readonly>
                    </div>
                </div>
                <div class="form-group">
                    <label>Contact Name</label>
                    <input type="text" value="Angel Santoyo" readonly>
                </div>
            </div>
            
            <div class="party-box">
                <h3>Buyer (Retailer)</h3>
                <div class="form-group">
                    <label>Business Name *</label>
                    <input type="text" placeholder="Enter business name" required>
                </div>
                <div class="form-group">
                    <label>Business Address *</label>
                    <input type="text" placeholder="Enter full address" required>
                </div>
                <div class="compact-row">
                    <div class="form-group">
                        <label>Contact Name *</label>
                        <input type="text" placeholder="Contact person" required>
                    </div>
                    <div class="form-group">
                        <label>Phone *</label>
                        <input type="tel" placeholder="(XXX) XXX-XXXX" required>
                    </div>
                </div>
                <div class="compact-row">
                    <div class="form-group">
                        <label>Email *</label>
                        <input type="email" placeholder="business@email.com" required>
                    </div>
                    <div class="form-group">
                        <label>Resale Certificate / Tax ID # *</label>
                        <input type="text" placeholder="Tax ID" required>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="section-title">Wholesale Terms & Conditions</div>
        
        <div class="terms-section">
            <div class="term-item">
                <div class="term-number">1</div>
                <h4>Wholesale Account Approval & Authorization</h4>
                <p>Buyer confirms that all products purchased from Seller are for resale purposes only. Seller reserves the right to approve, deny, suspend, or revoke wholesale status at its sole discretion. Buyer expressly authorizes and grants consent to Seller to list Buyer's business name, physical retail location, and website (if applicable) on Seller's official website, www.onlyskullz.com, as an authorized in-person retail location.</p>
            </div>
            
            <div class="term-item">
                <div class="term-number">2</div>
                <h4>Pricing (Website-Based Pricing)</h4>
                <p>Buyer acknowledges and agrees that wholesale pricing and product costs shall be based on, and reflect, the current retail pricing displayed on Seller's official website, www.onlyskullz.com, unless otherwise agreed in writing by Seller. Seller reserves the right to modify product pricing, wholesale pricing, and any applicable discounts at any time.</p>
            </div>
            
            <div class="highlight-box">
                <h4>Payment Due at Time of Order</h4>
                <p>Payment is due in full at the time the order is placed. No order will be processed, shipped, or released until payment has been received and cleared. Seller reserves the right to delay, suspend, or cancel any order if payment is not received.</p>
            </div>
            
            <div class="term-item">
                <div class="term-number">3</div>
                <h4>Wholesale Pricing Schedule & Product Requests</h4>
                <p>Buyer may request to carry any product currently listed on Seller's official website for resale at Buyer's physical retail location. All such requests are subject to Seller's prior review, pricing determination, availability, and written approval. Seller is under no obligation to approve all requested items. No product shall be authorized for resale unless pricing and availability have been mutually agreed upon.</p>
            </div>
            
            <div class="term-item">
                <div class="term-number">4</div>
                <h4>Payment Terms</h4>
                <p>Payment is due in full at the time the order is placed, unless otherwise agreed in writing by Seller. Accepted payment methods: Check (payable to Only Skullz LLC), PayPal (Angel@OnlySkullz.com), or other methods as agreed. Wholesale pricing is confidential and may not be disclosed to any third party without Seller's prior written consent.</p>
            </div>
            
            <div class="term-item">
                <div class="term-number">5</div>
                <h4>Shipping & Delivery</h4>
                <p>Shipping costs are the responsibility of Buyer unless otherwise agreed in writing. Seller is not responsible for delays caused by shipping carriers. <strong>Risk of loss transfers to Buyer once the order is delivered.</strong> Buyer bears all responsibility for inventory upon delivery.</p>
            </div>
            
            <div class="warning-box">
                <h4>All Sales Final Policy</h4>
                <p>All wholesale sales are final unless otherwise approved in writing. Damaged or incorrect items must be reported within three (3) days of delivery and must include photo documentation. Seller may issue a replacement or credit at its sole discretion. Buyer assumes full financial responsibility for all inventory upon delivery.</p>
            </div>
            
            <div class="term-item">
                <div class="term-number">6</div>
                <h4>Brand Use, Reselling & Promotional Efforts</h4>
                <p>Buyer agrees to not sell Only Skullz products on third-party marketplaces (including but not limited to Amazon, eBay, or Walmart Marketplace) without prior written approval. Buyer agrees not to alter logos, branding, packaging, or labels. Buyer agrees to make commercially reasonable efforts to promote the Only Skullz brand within Buyer's physical retail location, including display of QR codes, signage, and point-of-sale materials.</p>
            </div>
            
            <div class="term-item">
                <div class="term-number">7</div>
                <h4>Termination</h4>
                <p>Either party may terminate this Agreement with 30 days written notice. Seller may terminate immediately if Buyer violates pricing, resale, payment, or brand guidelines. Upon termination, all outstanding orders must be settled according to payment terms. Buyer remains responsible for all inventory purchased prior to termination.</p>
            </div>
            
            <div class="term-item">
                <div class="term-number">8</div>
                <h4>Governing Law</h4>
                <p>This Agreement shall be governed by and construed in accordance with the laws of the State of Illinois.</p>
            </div>
            
            <div class="term-item">
                <div class="term-number">9</div>
                <h4>Entire Agreement</h4>
                <p>This Agreement constitutes the entire agreement between the parties and supersedes all prior agreements, representations, or communications, whether written or oral. This is a wholesale purchase agreement, not a consignment arrangement. Title and risk of loss transfer to Buyer upon delivery of goods.</p>
            </div>
        </div>
        
        <!-- PAGE 2: Payment Instructions, Acknowledgments, and Signatures -->
        <div class="page-break"></div>
        
        <div class="header">
            <h1>Only Skullz <span class="wholesale-badge">Wholesale</span></h1>
            <p>Wholesale Purchase Agreement & Authorization Form - Page 2</p>
        </div>
        
        <div class="payment-instructions-section">
            <div class="section-title">Payment Instructions</div>
            
            <div class="form-group" style="margin-bottom: 12px;">
                <label style="margin-bottom: 8px;">Payment Method (check one):</label>
                <div style="display: flex; flex-wrap: wrap; gap: 10px;">
                    <div class="payment-option">
                        <input type="checkbox"> Check to Only Skullz LLC
                    </div>
                    <div class="payment-option">
                        <input type="checkbox"> PayPal: Angel@OnlySkullz.com
                    </div>
                    <div class="payment-option">
                        <input type="checkbox"> ACH Transfer
                    </div>
                    <div class="payment-option">
                        <input type="checkbox"> Credit Card
                    </div>
                    <div class="payment-option" style="min-width: 250px;">
                        <input type="checkbox"> Other:
                        <input type="text" placeholder="Specify payment method" style="flex: 1;">
                    </div>
                </div>
            </div>
            
            <div class="highlight-box" style="margin: 15px 0; padding: 10px;">
                <h4 style="font-size: 9pt;">Payment must clear before order processing begins</h4>
            </div>
        </div>
        
        <div class="section-title">Acknowledgments</div>
        
        <p style="font-size: 8pt; margin-bottom: 8px; color: #555;">By checking each box, Buyer acknowledges understanding and agreement:</p>
        
        <label class="checkbox-group">
            <input type="checkbox">
            <span>I understand that this is a wholesale purchase agreement, not a consignment arrangement. Title and risk of loss transfer to me upon delivery of goods.</span>
        </label>
        
        <label class="checkbox-group">
            <input type="checkbox">
            <span>I agree to remit payment in full at the time of order placement. I understand that no order will be processed until payment clears.</span>
        </label>
        
        <label class="checkbox-group">
            <input type="checkbox">
            <span>I understand that all wholesale sales are final, and I am responsible for reporting any damaged or incorrect items within three (3) days with photo documentation.</span>
        </label>
        
        <label class="checkbox-group">
            <input type="checkbox">
            <span>I confirm that all products will be sold only at the physical retail location specified above, not on third-party marketplaces or online without written approval.</span>
        </label>
        
        <label class="checkbox-group">
            <input type="checkbox">
            <span>I authorize Only Skullz LLC to list my business name and location on www.onlyskullz.com as an authorized retail location.</span>
        </label>
        
        <label class="checkbox-group">
            <input type="checkbox">
            <span>I understand that wholesale pricing is confidential and may not be disclosed to any third party without Seller's prior written consent.</span>
        </label>
        
        <label class="checkbox-group">
            <input type="checkbox">
            <span>I have read, understand, and agree to all terms and conditions outlined in this Wholesale Agreement.</span>
        </label>
        
        <div class="signature-grid">
            <div class="signature-box">
                <h4>Seller (Only Skullz LLC)</h4>
                <div class="compact-row">
                    <div class="form-group">
                        <label>Name</label>
                        <input type="text" value="Angel Santoyo" readonly style="text-align: center;">
                    </div>
                    <div class="form-group">
                        <label>Title</label>
                        <input type="text" value="Owner" readonly style="text-align: center;">
                    </div>
                </div>
                <div class="signature-line"></div>
                <div class="signature-label">Signature</div>
                <div class="form-group date-field">
                    <label>Date</label>
                    <input type="text" placeholder="MM/DD/YYYY" style="text-align: center;">
                </div>
            </div>
            
            <div class="signature-box">
                <h4>Buyer (Retailer)</h4>
                <div class="compact-row">
                    <div class="form-group">
                        <label>Business Name *</label>
                        <input type="text" placeholder="Business name" style="text-align: center;">
                    </div>
                    <div class="form-group">
                        <label>Signatory Name & Title *</label>
                        <input type="text" placeholder="Name, Title" style="text-align: center;">
                    </div>
                </div>
                <div class="signature-line"></div>
                <div class="signature-label">Signature</div>
                <div class="form-group date-field">
                    <label>Date *</label>
                    <input type="text" placeholder="MM/DD/YYYY" style="text-align: center;">
                </div>
            </div>
        </div>
        
        <!-- PAGE 3: Product List -->
        <div class="page-break"></div>
        
        <div class="header">
            <h1>Only Skullz <span class="wholesale-badge">Wholesale</span></h1>
            <p>Wholesale Product Submission List - Page 3</p>
        </div>
        
        <div class="section-title">Wholesale Product Request List</div>
        
        <div class="button-container">
            <button type="button" class="add-row-btn" onclick="addProductRow()">+ Add Product</button>
            <button type="button" class="remove-row-btn" onclick="removeProductRow()">- Remove Last</button>
        </div>
        
        <div class="table-container">
            <table class="product-table">
                <thead>
                    <tr>
                        <th style="width: 35%;">Item Description</th>
                        <th style="width: 18%;">Retail Price</th>
                        <th style="width: 15%;">Wholesale %</th>
                        <th style="width: 20%;" class="wholesale-col">Buyer's Price</th>
                        <th style="width: 12%;">Qty</th>
                        <th class="action-header" style="width: 10%;">Del</th>
                    </tr>
                </thead>
                <tbody id="productTableBody">
                    <tr>
                        <td><input type="text" class="product-name" placeholder="Product name"></td>
                        <td><input type="number" class="retail-price" placeholder="$0.00" step="0.01" oninput="calculateRow(this)"></td>
                        <td><input type="number" class="wholesale-pct" placeholder="%" step="1" min="0" max="100" oninput="calculateRow(this)"></td>
                        <td class="wholesale-col"><input type="text" class="buyer-price" placeholder="$0.00" readonly tabindex="-1"></td>
                        <td><input type="number" class="qty" placeholder="0" min="1"></td>
                        <td class="action-col"><button type="button" class="remove-row-btn" onclick="removeSpecificRow(this)" style="padding: 6px 10px; min-width: auto; width: auto;">x</button></td>
                    </tr>
                    <tr>
                        <td><input type="text" class="product-name" placeholder="Product name"></td>
                        <td><input type="number" class="retail-price" placeholder="$0.00" step="0.01" oninput="calculateRow(this)"></td>
                        <td><input type="number" class="wholesale-pct" placeholder="%" step="1" min="0" max="100" oninput="calculateRow(this)"></td>
                        <td class="wholesale-col"><input type="text" class="buyer-price" placeholder="$0.00" readonly tabindex="-1"></td>
                        <td><input type="number" class="qty" placeholder="0" min="1"></td>
                        <td class="action-col"><button type="button" class="remove-row-btn" onclick="removeSpecificRow(this)" style="padding: 6px 10px; min-width: auto; width: auto;">x</button></td>
                    </tr>
                    <tr>
                        <td><input type="text" class="product-name" placeholder="Product name"></td>
                        <td><input type="number" class="retail-price" placeholder="$0.00" step="0.01" oninput="calculateRow(this)"></td>
                        <td><input type="number" class="wholesale-pct" placeholder="%" step="1" min="0" max="100" oninput="calculateRow(this)"></td>
                        <td class="wholesale-col"><input type="text" class="buyer-price" placeholder="$0.00" readonly tabindex="-1"></td>
                        <td><input type="number" class="qty" placeholder="0" min="1"></td>
                        <td class="action-col"><button type="button" class="remove-row-btn" onclick="removeSpecificRow(this)" style="padding: 6px 10px; min-width: auto; width: auto;">x</button></td>
                    </tr>
                </tbody>
            </table>
        </div>
        
        <div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ccc; font-size: 8pt; color: #666; text-align: center;">
            <p>Only Skullz LLC - Aurora, IL - Angel@OnlySkullz.com - www.OnlySkullz.com</p>
        </div>
    </div>
    
    <button class="print-button" onclick="window.print()">Print / Save as PDF</button>
  `;

  const agreementCSS = `
    @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Inter:wght@300;400;600&display=swap');
    
    .wholesale-agreement-page * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
    }
    
    /* PRINT OPTIMIZATION */
    @page {
        size: letter;
        margin: 0.35in;
    }
    
    @page :first {
        margin-top: 0.2in;
    }
    
    .wholesale-agreement-page {
        font-family: 'Inter', sans-serif;
        background: #fff;
        color: #000;
        line-height: 1.4;
        font-size: 9pt;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }
    
    .wholesale-agreement-page .container {
        max-width: 7.5in;
        margin: 0 auto;
        background: #fff;
        padding: 0;
    }
    
    /* PREVENT PAGE BREAKS INSIDE ELEMENTS */
    .wholesale-agreement-page .party-box, 
    .wholesale-agreement-page .term-item, 
    .wholesale-agreement-page .checkbox-group, 
    .wholesale-agreement-page .signature-box, 
    .wholesale-agreement-page .product-table tr {
        page-break-inside: avoid;
    }
    
    /* FORCE PAGE BREAKS BEFORE MAJOR SECTIONS */
    .wholesale-agreement-page .page-break {
        page-break-before: always;
    }
    
    .wholesale-agreement-page .header {
        text-align: center;
        padding: 12px 0;
        border-bottom: 2px solid #8b0000;
        margin-bottom: 15px;
        page-break-after: avoid;
    }
    
    .wholesale-agreement-page .header h1 {
        font-family: 'Cinzel', serif;
        color: #000;
        font-size: 18pt;
        margin-bottom: 4px;
        text-transform: uppercase;
        letter-spacing: 2px;
    }
    
    .wholesale-agreement-page .header p {
        color: #444;
        font-size: 9pt;
        letter-spacing: 1px;
        text-transform: uppercase;
    }
    
    .wholesale-agreement-page .wholesale-badge {
        display: inline-block;
        color: #8b0000;
        padding: 2px 6px;
        font-size: 7pt;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-left: 4px;
        border: 1px solid #8b0000;
    }
    
    .wholesale-agreement-page .section-title {
        font-family: 'Cinzel', serif;
        color: #000;
        font-size: 11pt;
        margin: 15px 0 8px 0;
        padding-bottom: 4px;
        border-bottom: 2px solid #8b0000;
        text-transform: uppercase;
        letter-spacing: 1px;
        page-break-after: avoid;
    }
    
    .wholesale-agreement-page .party-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 15px;
        margin-bottom: 20px;
    }
    
    .wholesale-agreement-page .party-box {
        border: 1px solid #333;
        padding: 12px;
        background: #fafafa;
        border-radius: 4px;
    }
    
    .wholesale-agreement-page .party-box h3 {
        color: #000;
        margin-bottom: 10px;
        font-size: 9pt;
        text-transform: uppercase;
        letter-spacing: 1px;
        font-family: 'Cinzel', serif;
        border-bottom: 1px solid #8b0000;
        padding-bottom: 4px;
    }
    
    .wholesale-agreement-page .form-group {
        margin-bottom: 8px;
    }
    
    .wholesale-agreement-page label {
        display: block;
        color: #333;
        font-size: 7.5pt;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 2px;
    }
    
    .wholesale-agreement-page input[type="text"],
    .wholesale-agreement-page input[type="email"],
    .wholesale-agreement-page input[type="tel"],
    .wholesale-agreement-page input[type="date"],
    .wholesale-agreement-page input[type="number"],
    .wholesale-agreement-page select {
        width: 100%;
        padding: 6px 8px;
        border: 1px solid #999;
        font-family: 'Inter', sans-serif;
        font-size: 9pt;
        background: #fff;
        color: #000;
        height: 32px;
        border-radius: 3px;
        transition: border-color 0.2s ease;
    }
    
    .wholesale-agreement-page input[type="text"]:focus,
    .wholesale-agreement-page input[type="email"]:focus,
    .wholesale-agreement-page input[type="tel"]:focus,
    .wholesale-agreement-page input[type="date"]:focus,
    .wholesale-agreement-page input[type="number"]:focus,
    .wholesale-agreement-page select:focus {
        outline: none;
        border-color: #8b0000;
        box-shadow: 0 0 0 2px rgba(139, 0, 0, 0.1);
    }
    
    .wholesale-agreement-page input[readonly] {
        background: #f0f0f0;
        color: #555;
    }
    
    .wholesale-agreement-page .terms-section {
        border: 1px solid #ccc;
        padding: 15px;
        margin-bottom: 15px;
        background: #fafafa;
        border-radius: 4px;
    }
    
    .wholesale-agreement-page .term-item {
        margin-bottom: 12px;
        padding-left: 28px;
        position: relative;
    }
    
    .wholesale-agreement-page .term-number {
        position: absolute;
        left: 0;
        top: 0;
        background: #8b0000;
        color: #fff;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 8pt;
        font-weight: 700;
        font-family: 'Cinzel', serif;
        flex-shrink: 0;
    }
    
    .wholesale-agreement-page .term-item h4 {
        color: #000;
        margin-bottom: 4px;
        font-size: 9pt;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-family: 'Cinzel', serif;
    }
    
    .wholesale-agreement-page .term-item p {
        color: #333;
        line-height: 1.4;
        font-size: 8.5pt;
        text-align: left;
    }
    
    .wholesale-agreement-page .highlight-box {
        background: #f5f5f5;
        border: 1px solid #8b0000;
        color: #000;
        padding: 12px;
        margin: 15px 0;
        text-align: center;
        page-break-inside: avoid;
        border-radius: 4px;
    }
    
    .wholesale-agreement-page .highlight-box h4 {
        font-size: 9pt;
        margin-bottom: 6px;
        text-transform: uppercase;
        letter-spacing: 1px;
        font-family: 'Cinzel', serif;
        color: #8b0000;
    }
    
    .wholesale-agreement-page .highlight-box p {
        color: #333;
        line-height: 1.4;
        font-size: 8.5pt;
    }
    
    .wholesale-agreement-page .warning-box {
        background: #fff5f5;
        border: 1px solid #8b0000;
        border-left: 4px solid #8b0000;
        padding: 12px;
        margin: 15px 0;
        page-break-inside: avoid;
        border-radius: 0 4px 4px 0;
    }
    
    .wholesale-agreement-page .warning-box h4 {
        color: #8b0000;
        margin-bottom: 6px;
        font-family: 'Cinzel', serif;
        text-transform: uppercase;
        letter-spacing: 1px;
        font-size: 9pt;
    }
    
    .wholesale-agreement-page .warning-box p {
        color: #333;
        font-size: 8.5pt;
        line-height: 1.4;
    }
    
    .wholesale-agreement-page .checkbox-group {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        margin: 8px 0;
        padding: 10px;
        border: 1px solid #ccc;
        background: #fafafa;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.2s ease;
    }
    
    .wholesale-agreement-page .checkbox-group:hover {
        background: #f0f0f0;
    }
    
    .wholesale-agreement-page input[type="checkbox"] {
        width: 16px;
        height: 16px;
        margin-top: 2px;
        flex-shrink: 0;
        cursor: pointer;
        accent-color: #8b0000;
    }
    
    .wholesale-agreement-page .checkbox-group span {
        color: #333;
        font-size: 8.5pt;
        line-height: 1.3;
        flex: 1;
    }
    
    .wholesale-agreement-page .signature-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #333;
    }
    
    .wholesale-agreement-page .signature-box {
        text-align: center;
    }
    
    .wholesale-agreement-page .signature-box h4 {
        color: #000;
        margin-bottom: 10px;
        font-family: 'Cinzel', serif;
        text-transform: uppercase;
        letter-spacing: 1px;
        font-size: 9pt;
        border-bottom: 1px solid #8b0000;
        padding-bottom: 4px;
    }
    
    .wholesale-agreement-page .signature-line {
        border-bottom: 1px solid #000;
        height: 35px;
        margin-bottom: 4px;
        position: relative;
        background: #fff;
    }
    
    .wholesale-agreement-page .signature-label {
        font-weight: 600;
        color: #555;
        text-transform: uppercase;
        font-size: 7.5pt;
        letter-spacing: 1px;
    }
    
    .wholesale-agreement-page .date-field {
        margin-top: 8px;
    }
    
    /* PRODUCT TABLE - IMPROVED FOR ALL SCREENS */
    .wholesale-agreement-page .table-container {
        width: 100%;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        margin: 10px 0;
        border: 1px solid #ccc;
        border-radius: 4px;
    }
    
    .wholesale-agreement-page .product-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 8.5pt;
        min-width: 600px;
    }
    
    .wholesale-agreement-page .product-table thead {
        display: table-header-group;
    }
    
    .wholesale-agreement-page .product-table tr {
        page-break-inside: avoid;
    }
    
    .wholesale-agreement-page .product-table th {
        background: #f0f0f0;
        color: #000;
        padding: 8px 6px;
        text-align: left;
        font-weight: 700;
        text-transform: uppercase;
        font-size: 7.5pt;
        letter-spacing: 0.5px;
        border: 1px solid #999;
        border-bottom: 2px solid #8b0000;
        white-space: nowrap;
    }
    
    .wholesale-agreement-page .product-table td {
        padding: 6px;
        border: 1px solid #ccc;
        color: #000;
        background: #fff;
    }
    
    .wholesale-agreement-page .product-table input {
        border: 1px solid #999;
        padding: 6px;
        width: 100%;
        background: #fff;
        color: #000;
        font-size: 9pt;
        height: 32px;
        border-radius: 3px;
    }
    
    .wholesale-agreement-page .product-table input:focus {
        outline: none;
        border-color: #8b0000;
    }
    
    .wholesale-agreement-page .product-table th:nth-child(3),
    .wholesale-agreement-page .product-table td:nth-child(3),
    .wholesale-agreement-page .product-table th:nth-child(4),
    .wholesale-agreement-page .product-table td:nth-child(4),
    .wholesale-agreement-page .product-table th:nth-child(5),
    .wholesale-agreement-page .product-table td:nth-child(5) {
        text-align: center;
    }
    
    .wholesale-agreement-page .wholesale-col {
        background: #f9f9f9;
    }
    
    .wholesale-agreement-page .action-col {
        width: 50px;
        text-align: center;
    }
    
    .wholesale-agreement-page .skull-divider {
        text-align: center;
        color: #999;
        font-size: 10pt;
        margin: 15px 0;
        letter-spacing: 5px;
        page-break-after: avoid;
    }
    
    .wholesale-agreement-page strong {
        color: #000;
        font-weight: 700;
    }
    
    /* BUTTON STYLES - SCREEN ONLY */
    .wholesale-agreement-page .button-container {
        margin: 15px 0;
        text-align: center;
        display: flex;
        justify-content: center;
        gap: 10px;
        flex-wrap: wrap;
    }
    
    .wholesale-agreement-page .add-row-btn, 
    .wholesale-agreement-page .remove-row-btn {
        background: #8b0000;
        color: #fff;
        border: none;
        padding: 10px 20px;
        cursor: pointer;
        font-size: 9pt;
        font-family: 'Inter', sans-serif;
        border-radius: 4px;
        font-weight: 600;
        transition: all 0.2s ease;
        min-width: 120px;
    }
    
    .wholesale-agreement-page .remove-row-btn {
        background: #666;
    }
    
    .wholesale-agreement-page .add-row-btn:hover {
        background: #a00000;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }
    
    .wholesale-agreement-page .remove-row-btn:hover {
        background: #888;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }
    
    .wholesale-agreement-page .add-row-btn:active, 
    .wholesale-agreement-page .remove-row-btn:active {
        transform: translateY(0);
    }
    
    .wholesale-agreement-page .print-button {
        display: block;
        width: 200px;
        margin: 20px auto;
        padding: 12px;
        background: #8b0000;
        color: #fff;
        border: none;
        font-size: 11pt;
        cursor: pointer;
        text-align: center;
        font-family: 'Cinzel', serif;
        text-transform: uppercase;
        letter-spacing: 2px;
        border-radius: 4px;
        transition: all 0.2s ease;
        font-weight: 600;
    }
    
    .wholesale-agreement-page .print-button:hover {
        background: #a00000;
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0,0,0,0.2);
    }
    
    .wholesale-agreement-page .compact-row {
        display: flex;
        gap: 10px;
    }
    
    .wholesale-agreement-page .compact-row .form-group {
        flex: 1;
    }
    
    .wholesale-agreement-page .payment-option {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1;
        min-width: 200px;
        padding: 4px 0;
    }
    
    .wholesale-agreement-page .payment-option input[type="checkbox"] {
        margin-right: 4px;
    }
    
    .wholesale-agreement-page .payment-option input[type="text"] {
        flex: 1;
        margin-left: 4px;
    }
    
    /* MOBILE RESPONSIVE - SINGLE COLUMN */
    @media screen and (max-width: 768px) {
        .wholesale-agreement-page {
            font-size: 10pt;
            padding: 10px;
            background: #f5f5f5;
        }
        
        .wholesale-agreement-page .container {
            padding: 15px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        
        .wholesale-agreement-page .header h1 {
            font-size: 14pt;
            letter-spacing: 1px;
        }
        
        .wholesale-agreement-page .header p {
            font-size: 8pt;
        }
        
        .wholesale-agreement-page .wholesale-badge {
            font-size: 6pt;
            padding: 1px 4px;
        }
        
        /* SINGLE COLUMN PARTY GRID */
        .wholesale-agreement-page .party-grid {
            grid-template-columns: 1fr;
            gap: 15px;
        }
        
        .wholesale-agreement-page .party-box {
            padding: 15px;
        }
        
        .wholesale-agreement-page .compact-row {
            flex-direction: column;
            gap: 8px;
        }
        
        .wholesale-agreement-page .section-title {
            font-size: 10pt;
            margin-top: 20px;
        }
        
        .wholesale-agreement-page .term-item {
            padding-left: 32px;
        }
        
        .wholesale-agreement-page .term-number {
            width: 24px;
            height: 24px;
            font-size: 9pt;
        }
        
        .wholesale-agreement-page .term-item h4 {
            font-size: 9.5pt;
        }
        
        .wholesale-agreement-page .term-item p {
            font-size: 9pt;
            line-height: 1.5;
        }
        
        /* SINGLE COLUMN SIGNATURES */
        .wholesale-agreement-page .signature-grid {
            grid-template-columns: 1fr;
            gap: 25px;
        }
        
        .wholesale-agreement-page .signature-box {
            border: 1px solid #ddd;
            padding: 15px;
            border-radius: 4px;
            background: #fafafa;
        }
        
        .wholesale-agreement-page .checkbox-group {
            padding: 12px;
        }
        
        .wholesale-agreement-page .checkbox-group span {
            font-size: 9pt;
            line-height: 1.4;
        }
        
        .wholesale-agreement-page input[type="text"],
        .wholesale-agreement-page input[type="email"],
        .wholesale-agreement-page input[type="tel"],
        .wholesale-agreement-page input[type="number"] {
            height: 40px;
            font-size: 10pt;
        }
        
        .wholesale-agreement-page .payment-option {
            min-width: 100%;
            padding: 6px 0;
        }
        
        .wholesale-agreement-page .button-container {
            flex-direction: column;
            align-items: center;
        }
        
        .wholesale-agreement-page .add-row-btn, 
        .wholesale-agreement-page .remove-row-btn {
            width: 100%;
            max-width: 300px;
        }
        
        .wholesale-agreement-page .print-button {
            width: 100%;
            max-width: 300px;
        }
    }
    
    /* SMALL MOBILE */
    @media screen and (max-width: 480px) {
        .wholesale-agreement-page .container {
            padding: 10px;
        }
        
        .wholesale-agreement-page .header h1 {
            font-size: 12pt;
        }
        
        .wholesale-agreement-page .terms-section {
            padding: 12px;
        }
        
        .wholesale-agreement-page .highlight-box, 
        .wholesale-agreement-page .warning-box {
            padding: 10px;
        }
    }
    
    /* PRINT STYLES */
    @media print {
        /* Hide agreement page buttons and controls */
        .wholesale-agreement-page .print-button,
        .wholesale-agreement-page .button-container,
        .wholesale-agreement-page .add-row-btn,
        .wholesale-agreement-page .remove-row-btn,
        .wholesale-agreement-page .action-col,
        .wholesale-agreement-page .action-header {
            display: none !important;
        }
        
        /* Hide website navbar, footer, and other non-document elements globally */
        [data-testid="navbar"],
        nav,
        footer,
        body > div > nav,
        body > div > footer,
        body > div > header,
        .App > nav,
        .App > footer,
        [class*="Navbar"],
        [class*="Footer"],
        .purchase-notification,
        [class*="toaster"],
        [class*="sonner"],
        [class*="emergent"],
        .visitor-count,
        [class*="visitor"],
        .made-with {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            overflow: hidden !important;
        }
        
        /* Ensure clean print */
        html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
        }
        
        .App, .min-h-screen, .bg-void {
            background: white !important;
            min-height: auto !important;
        }
        
        main {
            padding: 0 !important;
            margin: 0 !important;
        }
        
        .wholesale-agreement-page {
            background: white;
            font-size: 8.5pt;
        }
        
        .wholesale-agreement-page .container {
            box-shadow: none;
            padding: 0;
            max-width: 100%;
        }
        
        .wholesale-agreement-page .party-grid {
            grid-template-columns: 1fr 1fr;
            gap: 12px;
        }
        
        .wholesale-agreement-page .compact-row {
            flex-direction: row;
        }
        
        .wholesale-agreement-page .signature-grid {
            grid-template-columns: 1fr 1fr;
        }
        
        .wholesale-agreement-page .table-container {
            overflow: visible;
            border: none;
        }
        
        .wholesale-agreement-page .product-table {
            min-width: auto;
        }
        
        .wholesale-agreement-page input {
            border: 1px solid #999 !important;
        }
    }
    
    @media screen {
        .wholesale-agreement-page {
            background: #e0e0e0;
            padding: 20px;
        }
        
        .wholesale-agreement-page .container {
            box-shadow: 0 0 20px rgba(0,0,0,0.1);
            padding: 30px;
            background: white;
            border-radius: 8px;
        }
    }
    
    /* TOUCH DEVICE OPTIMIZATIONS */
    @media (hover: none) {
        .wholesale-agreement-page .add-row-btn:hover, 
        .wholesale-agreement-page .remove-row-btn:hover, 
        .wholesale-agreement-page .print-button:hover {
            transform: none;
            box-shadow: none;
        }
        
        .wholesale-agreement-page .add-row-btn:active, 
        .wholesale-agreement-page .remove-row-btn:active, 
        .wholesale-agreement-page .print-button:active {
            transform: scale(0.98);
            opacity: 0.9;
        }
    }
    
    /* SMOOTH SCROLLING */
    .wholesale-agreement-page html {
        scroll-behavior: smooth;
    }
    
    /* FOCUS STATES FOR ACCESSIBILITY */
    .wholesale-agreement-page *:focus-visible {
        outline: 2px solid #8b0000;
        outline-offset: 2px;
    }
  `;

  useEffect(() => {
    // Add interactive JS functionality after component mounts
    const script = document.createElement('script');
    script.textContent = `
      // Add new product row with smooth animation
      window.addProductRow = function() {
          var tbody = document.getElementById('productTableBody');
          if (!tbody) return;
          var newRow = document.createElement('tr');
          newRow.style.opacity = '0';
          // Create cells safely using DOM methods
          var cells = [
            {type: 'text', className: 'product-name', placeholder: 'Product name'},
            {type: 'number', className: 'retail-price', placeholder: '$0.00', step: '0.01', oninput: 'calculateRow(this)'},
            {type: 'number', className: 'wholesale-pct', placeholder: '%', step: '1', min: '0', max: '100', oninput: 'calculateRow(this)'},
            {type: 'text', className: 'buyer-price', placeholder: '$0.00', readonly: true, tabindex: '-1', tdClass: 'wholesale-col'},
            {type: 'number', className: 'qty', placeholder: '0', min: '1'},
            {isButton: true, tdClass: 'action-col'}
          ];
          cells.forEach(function(cell) {
            var td = document.createElement('td');
            if (cell.tdClass) td.className = cell.tdClass;
            if (cell.isButton) {
              var btn = document.createElement('button');
              btn.type = 'button';
              btn.className = 'remove-row-btn';
              btn.textContent = 'x';
              btn.style.cssText = 'padding: 6px 10px; min-width: auto; width: auto;';
              btn.onclick = function() { removeSpecificRow(btn); };
              td.appendChild(btn);
            } else {
              var input = document.createElement('input');
              input.type = cell.type;
              input.className = cell.className;
              input.placeholder = cell.placeholder;
              if (cell.step) input.step = cell.step;
              if (cell.min) input.min = cell.min;
              if (cell.max) input.max = cell.max;
              if (cell.readonly) input.readOnly = true;
              if (cell.tabindex) input.tabIndex = cell.tabindex;
              if (cell.oninput) input.setAttribute('oninput', cell.oninput);
              td.appendChild(input);
            }
            newRow.appendChild(td);
          });
          tbody.appendChild(newRow);
          
          requestAnimationFrame(function() {
              newRow.style.transition = 'opacity 0.3s ease';
              newRow.style.opacity = '1';
          });
      };
      
      // Remove last product row
      window.removeProductRow = function() {
          var tbody = document.getElementById('productTableBody');
          if (!tbody) return;
          var rows = tbody.getElementsByTagName('tr');
          if (rows.length > 1) {
              var lastRow = rows[rows.length - 1];
              lastRow.style.transition = 'opacity 0.2s ease';
              lastRow.style.opacity = '0';
              setTimeout(function() {
                  tbody.removeChild(lastRow);
              }, 200);
          }
      };
      
      // Remove specific row
      window.removeSpecificRow = function(button) {
          var tbody = document.getElementById('productTableBody');
          if (!tbody) return;
          var rows = tbody.getElementsByTagName('tr');
          if (rows.length > 1) {
              var row = button.closest('tr');
              row.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
              row.style.opacity = '0';
              row.style.transform = 'translateX(-10px)';
              setTimeout(function() {
                  tbody.removeChild(row);
              }, 200);
          }
      };
      
      // Calculate row values
      window.calculateRow = function(input) {
          try {
              var row = input.closest('tr');
              var retailPrice = parseFloat(row.querySelector('.retail-price').value) || 0;
              var wholesalePct = parseFloat(row.querySelector('.wholesale-pct').value) || 0;
              var buyerPrice = row.querySelector('.buyer-price');
              
              if (retailPrice > 0 && wholesalePct >= 0 && wholesalePct <= 100) {
                  var discountAmount = retailPrice * (wholesalePct / 100);
                  var finalPrice = retailPrice - discountAmount;
                  buyerPrice.value = '$' + finalPrice.toFixed(2);
              } else {
                  buyerPrice.value = '';
              }
          } catch (e) {
              console.error('Calculation error:', e);
          }
      };
    `;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Sanitize HTML to prevent XSS
  const sanitizedHTML = useMemo(() => DOMPurify.sanitize(agreementHTML, {
    ADD_TAGS: ['button', 'input', 'style'],
    ADD_ATTR: ['onclick', 'oninput', 'onload', 'type', 'class', 'id', 'placeholder', 'step', 'min', 'max', 'readonly', 'tabindex', 'style', 'name', 'value']
  }), [agreementHTML]);

  return (
    <>
      <style>{agreementCSS}</style>
      <div 
        className="wholesale-agreement-page"
        dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
      />
    </>
  );
};

export default WholesaleAgreementPage;
